import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { opinlyTrack } from "@/lib/opinly-analytics";
import {
  getBookingForPayment,
  type PublicBookingForPayment,
} from "@/lib/payment.functions";

export const Route = createFileRoute("/thank-you/$token")({
  head: () => ({
    meta: [
      { title: "Bedankt voor uw betaling — Casa Mi Pajobe" },
      {
        name: "description",
        content:
          "Bevestiging van uw betaling voor uw verblijf in Casa Mi Pajobe.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ThankYouPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">Er ging iets mis</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground"
        >
          Opnieuw proberen
        </button>
      </Shell>
    );
  },
  notFoundComponent: () => (
    <Shell>
      <h1 className="text-2xl font-semibold">Link niet gevonden</h1>
    </Shell>
  ),
});

function fmt(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function ThankYouPage() {
  const { token } = Route.useParams();
  const fetchBooking = useServerFn(getBookingForPayment);

  const q = useQuery({
    queryKey: ["thank-you", token],
    queryFn: () => fetchBooking({ data: { token } }),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const d = query.state.data as any;
      return d && "paid" in d && !d.paid ? 2000 : false;
    },
  });

  const tracked = useRef(false);
  const paidBooking = q.data && "paid" in q.data ? (q.data as PublicBookingForPayment) : null;
  useEffect(() => {
    if (!paidBooking?.paid || tracked.current) return;
    tracked.current = true;
    const amountCents =
      paidBooking.kind === "deposit" ? paidBooking.depositCents : paidBooking.balanceCents;
    opinlyTrack(
      "purchase",
      { value: amountCents / 100, currency: paidBooking.currency.toUpperCase() },
      { externalEventId: `${paidBooking.reference}-${paidBooking.kind}` },
    );
  }, [paidBooking]);

  if (q.isLoading) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Laden…</p>
      </Shell>
    );
  }

  if (!q.data || ("notFound" in q.data && q.data.notFound)) {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold">Link niet gevonden</h1>
      </Shell>
    );
  }

  const b = q.data as PublicBookingForPayment;
  const isDeposit = b.kind === "deposit";
  const label = isDeposit ? "Voorschot (25%)" : "Eindsaldo (75%)";

  return (
    <Shell>
      <div className="mb-2 text-xs uppercase tracking-widest text-muted-foreground">
        Casa Mi Pajobe
      </div>

      <div className="mb-6 flex items-center gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-green-500/15 text-2xl">
          ✓
        </div>
        <div>
          <h1 className="font-display text-3xl font-semibold">Bedankt!</h1>
          <p className="text-sm text-muted-foreground">
            {b.paid
              ? `Uw ${label.toLowerCase()} is ontvangen.`
              : `We verwerken uw betaling. U ontvangt zo dadelijk een bevestigingsmail.`}
          </p>
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card p-5 text-sm">
        <Row k="Referentie" v={<span className="font-mono">{b.reference}</span>} />
        <Row k="Naam" v={b.guestName} />
        <Row k="Check-in" v={b.checkIn} />
        <Row k="Check-out" v={b.checkOut} />
        <Row k="Totaal verblijf" v={fmt(b.totalCents, b.currency)} />
        <Row k={label} v={fmt(isDeposit ? b.depositCents : b.balanceCents, b.currency)} />
        {b.paid && b.paidAt && (
          <Row k="Betaald op" v={b.paidAt.slice(0, 10)} />
        )}
      </div>

      {isDeposit && (
        <div className="mt-6 rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
          Uw boeking staat nu vast. Het eindsaldo van{" "}
          <strong className="text-foreground">{fmt(b.balanceCents, b.currency)}</strong>{" "}
          {b.balanceDueDate && (
            <>is te betalen tegen <strong className="text-foreground">{b.balanceDueDate}</strong>. </>
          )}
          U ontvangt hiervoor tijdig een betaallink per e-mail.
        </div>
      )}

      <div className="mt-8 text-center">
        <Link
          to="/"
          className="rounded-full border border-border px-5 py-2 text-sm hover:bg-muted"
        >
          Terug naar de website
        </Link>
      </div>
    </Shell>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-xl px-6 py-16">{children}</div>
    </div>
  );
}